import { useMemo, useState } from 'react'
import { JOURNAL_STATS, TRADES, fmtMoneyExact, pnlClass } from '../data/demo'
import { Page } from '../components/Layout'
import { Metric, StateBlock } from '../components/ui'

const ALL_SETUPS = [...new Set(TRADES.map((t) => t.setup))]
const ALL_TAGS = [...new Set(TRADES.flatMap((t) => t.tags))]

export function JournalPage() {
  const [setup, setSetup] = useState('ALL')
  const [tag, setTag] = useState('ALL')
  const [from, setFrom] = useState('2025-01-01')
  const [to, setTo] = useState('2026-12-31')
  const [noteDraft, setNoteDraft] = useState('')
  const [localNotes, setLocalNotes] = useState<Record<string, string>>({})
  const [selected, setSelected] = useState<string | null>(TRADES[0]?.id ?? null)

  const filtered = useMemo(() => {
    return TRADES.filter((t) => {
      const d = t.closeTime.slice(0, 10)
      if (d < from || d > to) return false
      if (setup !== 'ALL' && t.setup !== setup) return false
      if (tag !== 'ALL' && !t.tags.includes(tag)) return false
      return true
    })
  }, [setup, tag, from, to])

  const selectedTrade = filtered.find((t) => t.id === selected) ?? filtered[0] ?? null

  const sessionBuckets = useMemo(() => {
    const m = new Map<string, { pnl: number; n: number }>()
    for (const t of filtered) {
      const cur = m.get(t.session) ?? { pnl: 0, n: 0 }
      cur.pnl += t.pnl
      cur.n += 1
      m.set(t.session, cur)
    }
    return [...m.entries()]
  }, [filtered])

  function reset() {
    setSetup('ALL')
    setTag('ALL')
    setFrom('2025-01-01')
    setTo('2026-12-31')
    setLocalNotes({})
    setNoteDraft('')
    setSelected(TRADES[0]?.id ?? null)
  }

  return (
    <Page wide>
      <div className="app-header">
        <div>
          <div className="chip-row" style={{ marginBottom: 8 }}>
            <span className="badge demo">DEMO behavior lab</span>
            <span className="badge">Local only · no AI service</span>
          </div>
          <h1>Atlas Journal Lab</h1>
          <p className="muted" style={{ margin: '0.35rem 0 0' }}>
            Rule adherence, mistake taxonomy, revenge/overtrading signals — storage with teeth.
          </p>
        </div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={reset}>
          Reset controls
        </button>
      </div>

      <div className="grid-metrics" style={{ marginBottom: '1rem' }}>
        <div className="card card-pad">
          <Metric label="Rule adherence" value={`${JOURNAL_STATS.ruleAdherence}%`} tone="lime" />
        </div>
        <div className="card card-pad">
          <Metric
            label="Revenge score"
            value={`${JOURNAL_STATS.revengeScore}`}
            tone={JOURNAL_STATS.revengeScore > 25 ? 'danger' : 'amber'}
            sub="lower is better"
          />
        </div>
        <div className="card card-pad">
          <Metric
            label="Overtrading score"
            value={`${JOURNAL_STATS.overtradingScore}`}
            tone="amber"
            sub="lower is better"
          />
        </div>
        <div className="card card-pad">
          <Metric label="Best setup" value={JOURNAL_STATS.bestSetup} />
        </div>
        <div className="card card-pad">
          <Metric label="Top mistake" value={JOURNAL_STATS.worstMistake} tone="danger" />
        </div>
        <div className="card card-pad">
          <Metric label="Trades in view" value={String(filtered.length)} />
        </div>
      </div>

      <div className="journal-layout">
        <aside className="card card-pad stack">
          <label>
            <div className="mute" style={{ marginBottom: 4 }}>
              From
            </div>
            <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label>
            <div className="mute" style={{ marginBottom: 4 }}>
              To
            </div>
            <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <label>
            <div className="mute" style={{ marginBottom: 4 }}>
              Setup taxonomy
            </div>
            <select className="select" value={setup} onChange={(e) => setSetup(e.target.value)}>
              <option value="ALL">All setups</option>
              {ALL_SETUPS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <div>
            <div className="mute" style={{ marginBottom: 6 }}>
              Tags
            </div>
            <div className="chip-row">
              <button type="button" className={`chip ${tag === 'ALL' ? 'active' : ''}`} aria-pressed={tag === 'ALL'} onClick={() => setTag('ALL')}>
                all
              </button>
              {ALL_TAGS.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`chip ${tag === t ? 'active' : ''}`}
                  aria-pressed={tag === t}
                  onClick={() => setTag(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="card card-pad" style={{ background: 'var(--bg-0)' }}>
            <h3 className="display" style={{ marginTop: 0, fontSize: '1rem' }}>
              What changed?
            </h3>
            <ul className="muted" style={{ margin: 0, paddingLeft: '1.1rem' }}>
              {JOURNAL_STATS.whatChanged.map((w) => (
                <li key={w} style={{ marginBottom: 6 }}>
                  {w}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="display" style={{ marginTop: 0, fontSize: '1rem' }}>
              Session analysis
            </h3>
            {sessionBuckets.map(([name, v]) => (
              <div key={name} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                <span>{name}</span>
                <span className={`mono ${pnlClass(v.pnl)}`}>
                  {fmtMoneyExact(v.pnl)} · {v.n}
                </span>
              </div>
            ))}
          </div>
        </aside>

        <div className="stack">
          <div className="card card-pad">
            <h2 className="display" style={{ marginTop: 0, fontSize: '1.2rem' }}>
              Weekly review
            </h2>
            <p className="muted">
              Adherence {JOURNAL_STATS.ruleAdherence}% with revenge score {JOURNAL_STATS.revengeScore}. Filtered set
              shows {filtered.filter((t) => t.pnl > 0).length} winners /{' '}
              {filtered.filter((t) => t.pnl <= 0).length} losers. Focus: protect {JOURNAL_STATS.bestSetup}; eliminate
              “{JOURNAL_STATS.worstMistake}”.
            </p>
            <div className="notice ok">No external AI and no server persistence — notes stay in this browser tab session.</div>
          </div>

          <div className="card card-pad">
            <h2 className="display" style={{ marginTop: 0, fontSize: '1.2rem' }}>
              Trade journal
            </h2>
            {filtered.length === 0 ? (
              <StateBlock mode="empty" />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Close</th>
                      <th>Symbol</th>
                      <th>Setup</th>
                      <th>Mistakes</th>
                      <th className="num">P&amp;L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((t) => (
                      <tr
                        key={t.id}
                        tabIndex={0}
                        aria-selected={selectedTrade?.id === t.id}
                        onClick={() => {
                          setSelected(t.id)
                          setNoteDraft(localNotes[t.id] ?? t.notes)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setSelected(t.id)
                            setNoteDraft(localNotes[t.id] ?? t.notes)
                          }
                        }}
                        style={selectedTrade?.id === t.id ? { background: 'rgba(184,255,60,0.06)' } : undefined}
                      >
                        <td className="mono">{t.closeTime.slice(0, 10)}</td>
                        <td>{t.symbol}</td>
                        <td>{t.setup}</td>
                        <td>{t.mistakes[0] ?? t.ruleBreaks[0] ?? '—'}</td>
                        <td className={`num mono ${pnlClass(t.pnl)}`}>{fmtMoneyExact(t.pnl)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {selectedTrade ? (
            <div className="card card-pad">
              <div className="cc-top">
                <h2 className="display" style={{ margin: 0, fontSize: '1.2rem' }}>
                  {selectedTrade.id} · {selectedTrade.symbol} {selectedTrade.side}
                </h2>
                <span className={`mono ${pnlClass(selectedTrade.pnl)}`}>{fmtMoneyExact(selectedTrade.pnl)}</span>
              </div>
              <div className="chip-row" style={{ margin: '0.75rem 0' }}>
                {selectedTrade.tags.map((t) => (
                  <span key={t} className="badge">
                    {t}
                  </span>
                ))}
                {selectedTrade.mistakes.map((m) => (
                  <span key={m} className="badge danger">
                    {m}
                  </span>
                ))}
                {selectedTrade.ruleBreaks.map((m) => (
                  <span key={m} className="badge warn">
                    {m}
                  </span>
                ))}
              </div>
              <div className="split-2">
                <label style={{ display: 'grid', gap: 6 }}>
                  <span className="mute">Notes</span>
                  <textarea
                    className="textarea"
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() =>
                      setLocalNotes((n) => ({
                        ...n,
                        [selectedTrade.id]: noteDraft,
                      }))
                    }
                  >
                    Save note locally
                  </button>
                </label>
                <div>
                  <div className="mute">Screenshot placeholder</div>
                  <div
                    className="panel"
                    style={{
                      marginTop: 6,
                      minHeight: 160,
                      display: 'grid',
                      placeItems: 'center',
                      color: 'var(--text-mute)',
                      borderStyle: 'dashed',
                    }}
                  >
                    {selectedTrade.screenshot ? 'Chart capture slot · DEMO' : 'No capture attached'}
                  </div>
                  <p className="mute" style={{ fontSize: '0.85rem' }}>
                    {selectedTrade.session} session · {selectedTrade.weekday} · {selectedTrade.rMultiple}R
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </Page>
  )
}
